import React from 'react'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { ChatBubbleBottomCenterTextIcon, Cog6ToothIcon } from '@heroicons/react/24/outline'

import { AriaInteractionMode } from '../../../../../../../pure-js/types/AppAssistant'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { TESTIDS } from '../../../../../config/testids'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { AriaBrand, AriaBrandVariant } from '../../../../../shared/components/AriaBrand'
import { AriaLanguagePicker } from '../../../../../shared/components/AriaLanguagePicker'
import { AriaModeSwitch } from '../../../../../shared/components/AriaModeSwitch'
import { BetaTag } from '../../../../../shared/components/BetaTag'
import { AriaFace } from './AriaFace'

type CoachPageHeaderProps = {
  speaking: boolean
  mode: AriaInteractionMode
  onModeChange: (mode: AriaInteractionMode) => void
  onLanguageChanged: () => void
  onOpenFeedback: () => void
  onOpenSettings: () => void
  /** The leader's Individual / Leadership switch; nobody else is offered the choice. */
  focusSwitch?: React.ReactNode
}

const HEADER_ORB_SIZE_PX = 48

/** One Aria avatar per page — this is it. */
export const CoachPageHeader = (props: CoachPageHeaderProps) => {
  const { speaking, mode, onModeChange, onLanguageChanged, onOpenFeedback, onOpenSettings, focusSwitch } = props
  const { t } = useTranslation()

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row', md: 'row' }}
      spacing={{ xs: 1.5, sm: 2, md: 2 }}
      alignItems={{ xs: 'flex-start', sm: 'center', md: 'center' }}
      justifyContent="space-between"
      sx={{ flexShrink: 0 }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <AriaFace speaking={speaking} size={HEADER_ORB_SIZE_PX} />
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 800, fontSize: 22 }}>
              <AriaBrand variant={AriaBrandVariant.COACH} />
            </Typography>
            <BetaTag />
          </Stack>
          <Typography sx={{ color: INTERAX_COLORS.thunderGrey }}>{t('gamer.ariaCoach.subtitle')}</Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        {focusSwitch}
        <AriaModeSwitch mode={mode} onChange={onModeChange} />
        <AriaLanguagePicker onLanguageChanged={onLanguageChanged} />
        <Tooltip title={t('gamer.ariaCoach.openFeedback')}>
          <IconButton
            onClick={onOpenFeedback}
            aria-label={t('gamer.ariaCoach.openFeedback')}
            data-testid={TESTIDS.COACH_OPEN_FEEDBACK_BTN}
            sx={{ color: INTERAX_COLORS.thunderGrey }}
          >
            <ChatBubbleBottomCenterTextIcon width={24} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('gamer.ariaCoach.settings')}>
          <IconButton
            onClick={onOpenSettings}
            aria-label={t('gamer.ariaCoach.settings')}
            sx={{ color: INTERAX_COLORS.thunderGrey }}
          >
            <Cog6ToothIcon width={24} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  )
}
